/**
 * Devamsız vardiya karar servisi.
 *
 * BOSS, durum='devamsiz' işaretlenmiş vardiyalar için 3 karar verir:
 *
 *  - 'onaylandi'   → Vardiya durum='devamsiz' KALIR; sadece devamsizKarari yazılır.
 *                    Mali aksiyon: maas-hesap.ts'in `noShowCezasi` mekanizması
 *                    zaten otomatik kesinti yapıyor (10 saat × saatlik karşılık).
 *                    Bu serviste EK PUANTAJ YAZILMAZ — çift kesintiye yol açar.
 *
 *  - 'izinSayildi' → Vardiya durum='izinli' yapılır → no-show sayımından çıkar
 *                    (vardiya-kontrol-service izinli vardiyaları no-show saymıyor).
 *                    Ayrıca sorumluIzinAtamasi ile 1 günlük izin kaydı oluşturulur.
 *                    Kaynak (yıllık/aylık/maas) BOSS modal'dan seçer.
 *
 *  - 'mazeret'     → Vardiya durum='iptal' yapılır → no-show sayımından çıkar.
 *                    Hiçbir mali aksiyon yok (spec: "Mali aksiyon yok").
 *
 * Her durumda personele sistem-içi bildirim.
 */

import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import { type Vardiya, type DevamsizKararTipi } from './vardiya-service'
import { sorumluIzinAtamasi, type IzinKaynagi } from './izin-service'
import { bildirimGonder } from './bildirim-service'
import type { KullaniciPersonel } from '@/types'

const VARDIYALAR = 'vardiyalar'
const KULLANICILAR = 'kullanicilar'

/** Vardiya süresi (dakika), gece taşması dahil — DevamsizKararModal göstermek için. */
function dk(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}
export function vardiyaSuresiDakika(vardiya: Vardiya): number {
  const s = dk(vardiya.baslangic)
  let b = dk(vardiya.bitis)
  if (b <= s) b += 24 * 60
  return b - s
}

async function personelGetir(personelId: string): Promise<KullaniciPersonel | null> {
  const db = getFirestoreDb()
  const s = await getDoc(doc(db, KULLANICILAR, personelId))
  if (!s.exists()) return null
  return { id: s.id, ...(s.data() as object) } as KullaniciPersonel
}

async function vardiyaTamGetir(vardiyaId: string): Promise<Vardiya | null> {
  const db = getFirestoreDb()
  const s = await getDoc(doc(db, VARDIYALAR, vardiyaId))
  if (!s.exists()) return null
  return { id: s.id, ...(s.data() as object) } as Vardiya
}

/** "YYYY-MM-DD" + n gün → "YYYY-MM-DD" */
function tarihEkle(tarih: string, gun: number): string {
  const [y, m, d] = tarih.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  dt.setDate(dt.getDate() + gun)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`
}

/** "YYYY-MM-DD" → "DD.MM.YYYY" */
function tarihEtiketi(tarih: string): string {
  return tarih.split('-').reverse().join('.')
}

export interface DevamsizKararData {
  vardiyaId: string
  karar: DevamsizKararTipi
  kararVerenId: string
  /** İzin say için kaynak (yıllık/aylık/maas) — zorunlu */
  izinKaynagi?: IzinKaynagi
}

/**
 * Devamsız karar uygula.
 */
export async function devamsizKararVer(data: DevamsizKararData): Promise<void> {
  const db = getFirestoreDb()
  const vardiya = await vardiyaTamGetir(data.vardiyaId)
  if (!vardiya) throw new Error('Vardiya bulunamadı.')
  if (vardiya.durum !== 'devamsiz') {
    throw new Error('Bu vardiya devamsız işaretli değil.')
  }
  if (vardiya.devamsizKarari) {
    throw new Error('Bu vardiya için karar zaten verilmiş.')
  }

  const personel = await personelGetir(vardiya.personelId)
  if (!personel) throw new Error('Personel bulunamadı.')

  let izinTalepId: string | undefined

  // Vardiya güncelleme objesi (ortak alanlar)
  const update: Record<string, unknown> = {
    devamsizKarari: data.karar,
    devamsizKararTarihi: nowServer(),
    devamsizKararVerenId: data.kararVerenId,
    guncellenmeTarihi: nowServer(),
  }

  if (data.karar === 'onaylandi') {
    // Vardiya durum='devamsiz' KALIR.
    // noShowCezasi (maas-hesap.ts) zaten otomatik 10 saat × saatlik kesiyor.
    // BURADA EK PUANTAJ YAZILMAZ — çift kesintiye yol açar.
  } else if (data.karar === 'izinSayildi') {
    if (!data.izinKaynagi) {
      throw new Error('İzin say kararı için kaynak belirtilmeli (yıllık/aylık/maas).')
    }
    // 1 günlük izin: baslangic = vardiya tarihi, bitis = ertesi gün (işbaşı kuralı)
    izinTalepId = await sorumluIzinAtamasi({
      personelId: vardiya.personelId,
      baslangicTarihi: vardiya.tarih,
      bitisTarihi: tarihEkle(vardiya.tarih, 1),
      aciklama: `Devamsızlık → İzin say (BOSS kararı)`,
      atayanId: data.kararVerenId,
      kaynak: data.izinKaynagi,
      dagilim: {
        yillik: data.izinKaynagi === 'yillik' ? 1 : 0,
        aylik: data.izinKaynagi === 'aylik' ? 1 : 0,
        maas: data.izinKaynagi === 'maas' ? 1 : 0,
      },
    })
    // Vardiya artık 'izinli' → no-show sayımından çıkar
    update.durum = 'izinli'
    update.devamsizIzinKaynagi = data.izinKaynagi
    if (izinTalepId) update.devamsizIzinTalepId = izinTalepId
  } else if (data.karar === 'mazeret') {
    // Vardiya 'iptal' → no-show sayımından çıkar
    update.durum = 'iptal'
  }

  await updateDoc(doc(db, VARDIYALAR, data.vardiyaId), update)

  // Personele bildirim
  const tEtiket = tarihEtiketi(vardiya.tarih)
  let baslik = ''
  let mesaj = ''
  if (data.karar === 'onaylandi') {
    baslik = '⚠️ Devamsızlık onaylandı'
    mesaj = `${tEtiket} vardiyasına gelmeme durumu onaylandı. Sistem otomatik 1 günlük mesai bedelini maaştan keser.`
  } else if (data.karar === 'izinSayildi') {
    const k =
      data.izinKaynagi === 'yillik'
        ? 'yıllık izin'
        : data.izinKaynagi === 'aylik'
        ? 'aylık izin hakkı'
        : 'maaştan kesinti'
    baslik = '🟢 Devamsızlık izin sayıldı'
    mesaj = `${tEtiket} vardiyasına gelmeme durumu için ${k} uygulandı.`
  } else {
    baslik = '🔵 Devamsızlık mazeret kabul'
    mesaj = `${tEtiket} vardiyasına gelmeme durumu mazeret olarak kabul edildi.`
  }

  try {
    await bildirimGonder({
      kullaniciId: vardiya.personelId,
      tipi: 'noShow3saat',
      aciliyet: data.karar === 'onaylandi' ? 'kritik' : 'normal',
      baslik,
      mesaj,
      link: '/personel',
      ilgiliKayit: { koleksiyon: 'vardiyalar', id: data.vardiyaId },
    })
  } catch (e) {
    console.warn('[devamsiz] personel bildirimi gönderilemedi:', e)
  }

  // personel referansı modal'da kullanılıyor olabilir; burada gereksiz
  void personel
}
